/**
 * Reading and rendering date cells.
 *
 * A date cell is stored as an ISO string in UTC and displayed in a timezone the
 * field chooses, in one of three date formats and two time formats. Every date
 * filter and the human-readable rendering both go through here, so the two can
 * never disagree about what a cell says.
 *
 * Implemented on `Intl.DateTimeFormat` rather than a date library. The whole
 * requirement is "these six format combinations, in an IANA timezone", which the
 * platform does natively and correctly, including the historical offset rules a
 * hand-rolled version gets wrong.
 */
import { TZDate } from '@date-fns/tz'

import type { DateFormat, TimeFormat } from '@/api/types'

export type { DateFormat, TimeFormat }

/** The subset of a field this module reads. */
export interface DateFieldLike {
  date_include_time?: boolean | null
  date_format?: DateFormat | null
  date_time_format?: TimeFormat | null
  date_force_timezone?: string | null
}

/**
 * How each date format orders its parts.
 *
 * `humanFormat` is the placeholder shown in an input, lowercase by convention so
 * it reads as a hint rather than a value.
 */
export const DATE_FORMATS = {
  EU: { parts: ['day', 'month', 'year'], separator: '/', humanFormat: 'dd/mm/yyyy' },
  US: { parts: ['month', 'day', 'year'], separator: '/', humanFormat: 'mm/dd/yyyy' },
  ISO: { parts: ['year', 'month', 'day'], separator: '-', humanFormat: 'yyyy-mm-dd' },
} as const satisfies Record<
  DateFormat,
  {
    parts: readonly ('day' | 'month' | 'year')[]
    separator: string
    humanFormat: string
  }
>

export const TIME_FORMATS = {
  12: { hour12: true, humanFormat: 'hh:mm AM' },
  24: { hour12: false, humanFormat: 'hh:mm' },
} as const satisfies Record<TimeFormat, { hour12: boolean; humanFormat: string }>

/** The placeholder shown under a date input. */
export function getDateHumanReadableFormat(format: DateFormat): string {
  return DATE_FORMATS[format].humanFormat
}

/** The placeholder shown under a time input. */
export function getTimeHumanReadableFormat(format: TimeFormat): string {
  return TIME_FORMATS[format].humanFormat
}

/**
 * The timezone a field's values should be read in.
 *
 * `null` for a date-only field: it has no time, so it has no timezone, and
 * shifting it by one would move it across a day boundary. A field that includes
 * time either pins a timezone or follows the viewer's -- so the same cell reads
 * differently to two people, which is the intent.
 *
 * `guess` exists for search indexing, which must produce the same text for
 * everyone and so cannot fall back to the viewer's zone.
 */
export function getFieldTimezone(field: DateFieldLike, guess = true): string | null {
  if (!field.date_include_time) return null
  if (field.date_force_timezone) return field.date_force_timezone
  if (!guess) return null
  return Intl.DateTimeFormat().resolvedOptions().timeZone
}

/**
 * Parses a stored date value into a `Date`, or null if it is not a date.
 *
 * A datetime with no timezone designator is read as UTC. Left alone, the
 * platform reads a bare `YYYY-MM-DDTHH:mm:ss` as *local* time while a bare
 * `YYYY-MM-DD` is read as UTC -- so the same field would shift by the viewer's
 * offset depending on whether the backend included a `Z`.
 */
export function parseDateValue(value: unknown): Date | null {
  if (value === null || value === undefined || value === '') return null
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value
  // A number is milliseconds since the epoch. Stringifying it first would make
  // `0` parse as the year 2000 rather than as 1970.
  if (typeof value === 'number') {
    const fromEpoch = new Date(value)
    return Number.isNaN(fromEpoch.getTime()) ? null : fromEpoch
  }
  if (typeof value !== 'string') return null

  let text = value
  if (/\d{2}:\d{2}/.test(text) && !/(Z|[+-]\d{2}:?\d{2})$/i.test(text)) {
    text = `${text}Z`
  }
  const date = new Date(text)
  return Number.isNaN(date.getTime()) ? null : date
}

/** The calendar parts of an instant, as read in a given timezone. */
interface DateParts {
  year: string
  month: string
  day: string
  hour: string
  minute: string
  dayPeriod: string
}

function partsIn(date: Date, timezone: string | null, hour12: boolean): DateParts {
  const formatter = new Intl.DateTimeFormat('en-US', {
    // A date-only field has no timezone of its own; its instant is UTC midnight
    // and must be read back as UTC or it lands on the previous day west of
    // Greenwich.
    timeZone: timezone ?? 'UTC',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12,
  })
  const out: Record<string, string> = {}
  for (const { type, value } of formatter.formatToParts(date)) {
    out[type] = value
  }
  return {
    year: out.year ?? '',
    month: out.month ?? '',
    day: out.day ?? '',
    // In 12-hour mode `en-US` renders midnight as 24 under some ICU versions
    // rather than 12; normalise so the two formats agree on what hour it is.
    hour: hour12 && out.hour === '24' ? '12' : (out.hour ?? ''),
    minute: out.minute ?? '',
    dayPeriod: out.dayPeriod ?? '',
  }
}

/**
 * Renders a date cell the way the grid shows it.
 *
 * An unparseable or absent value renders as the empty string rather than as
 * "Invalid date": this text is what the `contains` filters match against, and a
 * placeholder would be matchable.
 */
export function formatDateValue(
  field: DateFieldLike,
  value: unknown,
  guessTimezone = true,
): string {
  const date = parseDateValue(value)
  if (date === null) return ''

  const timezone = getFieldTimezone(field, guessTimezone)
  const dateFormat = DATE_FORMATS[field.date_format ?? 'ISO']
  const timeFormat = TIME_FORMATS[field.date_time_format ?? '24']
  const parts = partsIn(date, timezone, timeFormat.hour12)

  const rendered = dateFormat.parts.map((part) => parts[part]).join(dateFormat.separator)

  if (!field.date_include_time) return rendered

  const time = `${parts.hour}:${parts.minute}`
  return timeFormat.hour12
    ? `${rendered} ${time} ${parts.dayPeriod.toUpperCase()}`
    : `${rendered} ${time}`
}

/* ------------------------------------------------------------------ input */

/**
 * Reading a date back out of what somebody typed or pasted.
 *
 * The rule the whole of this follows: **text carrying a timezone designator
 * names an instant, and text without one names a wall clock in the field's
 * timezone.** That is the only reading under which a value taken out of the
 * API and pasted back in is the same moment, and under which typing "14:30"
 * into a cell that displays Amsterdam time means half past two in Amsterdam.
 *
 * Upstream reinterprets even a designator-bearing string as a wall clock, so
 * pasting `2024-03-05T14:30:00Z` into an Amsterdam field stores half past one.
 * That is a deliberate departure.
 */
/** Text that names an instant: a full ISO timestamp with an offset or a Z. */
const ISO_INSTANT =
  /^\d{4}-\d{2}-\d{2}[T ]\d{1,2}:\d{2}(:\d{2}(\.\d+)?)?\s*(Z|[+-]\d{2}:?\d{2})$/i

/**
 * A time at the end of the text, and the date is whatever came before it.
 *
 * Preceded by a separator rather than allowed to start the string, so that a
 * time on its own is not read as a date: there is no day for it to land on.
 */
const TIME_AT_END = /[T ](\d{1,2}):(\d{2})(?::(\d{2}))?\s*(a\.?m?\.?|p\.?m?\.?)?$/i

/**
 * Three numbers and one separator, the same one twice.
 *
 * The backreference is what stops `05/03-2024` parsing: a value punctuated two
 * different ways is not a date somebody meant to write.
 */
const DATE_PARTS = /^(\d{1,4})([-/.])(\d{1,2})\2(\d{1,4})$/

/** A calendar reading, before it is placed in a timezone. */
interface WallClock {
  year: number
  month: number
  day: number
  hour: number
  minute: number
  second: number
}

/**
 * A two-digit year, on the pivot every spreadsheet and `strptime` uses.
 *
 * 68 is 2068 and 69 is 1969. Guessing is unavoidable here -- the text is
 * genuinely ambiguous -- and following the convention everything else follows
 * is better than refusing a form that half the world's exports are written in.
 */
function expandYear(text: string): number | null {
  if (text.length === 4) return Number(text)
  if (text.length > 2) return null
  const short = Number(text)
  return short < 69 ? 2000 + short : 1900 + short
}

/**
 * Whether a year, month and day name a day that exists.
 *
 * The month coming back unchanged is the whole of the check, and that is not a
 * shortcut: a day the calendar does not have rolls into another month -- the
 * thirty-first of February is the third of March, the zeroth of February is
 * the thirty-first of January -- and so does a month outside one to twelve.
 * Nothing three digits of day can reach rolls by a whole year without changing
 * the month on the way.
 *
 * Set rather than constructed: `new Date(24, ...)` is 1924, and a year is read
 * here exactly as it was written.
 */
function isRealDay(year: number, month: number, day: number): boolean {
  const probe = new Date(0)
  probe.setUTCFullYear(year, month - 1, day)
  return probe.getUTCMonth() === month - 1
}

/**
 * The day a date names, in the field's own order and then in the other one.
 *
 * Four digits first means the year comes first, whatever the field's format
 * says -- nobody writing `2024/03/05` means anything else. Otherwise the year
 * is last and the two before it are read the way the field displays them,
 * falling back to the other order when that names no real day. That fallback
 * is upstream's and it is what lets `13/05/2024` parse in a field that shows
 * American dates.
 */
function parseDay(
  text: string,
  format: DateFormat,
): { year: number; month: number; day: number } | null {
  const match = DATE_PARTS.exec(text)
  if (match === null) return null

  const [, first = '', , middle = '', last = ''] = match

  if (first.length === 4) {
    const year = Number(first)
    const month = Number(middle)
    const day = Number(last)
    return isRealDay(year, month, day) ? { year, month, day } : null
  }

  const year = expandYear(last)
  if (year === null) return null

  const a = Number(first)
  const b = Number(middle)
  const [month, day] = format === 'US' ? [a, b] : [b, a]
  if (isRealDay(year, month, day)) return { year, month, day }
  return isRealDay(year, day, month) ? { year, month: day, day: month } : null
}

/** The time a match names, on a twenty-four hour clock. */
function parseClock(
  match: RegExpExecArray,
): { hour: number; minute: number; second: number } | null {
  const [, rawHour = '', rawMinute = '', rawSecond, meridiem] = match

  let hour = Number(rawHour)
  const minute = Number(rawMinute)
  const second = rawSecond === undefined ? 0 : Number(rawSecond)

  if (meridiem !== undefined) {
    // On a twelve-hour clock only one to twelve mean anything, and midnight is
    // written as twelve rather than as zero.
    if (hour < 1 || hour > 12) return null
    const afternoon = meridiem.toLowerCase().startsWith('p')
    hour = afternoon ? (hour === 12 ? 12 : hour + 12) : hour === 12 ? 0 : hour
  }

  if (hour > 23 || minute > 59 || second > 59) return null
  return { hour, minute, second }
}

/**
 * The instant a piece of text names, or null when it names none.
 *
 * Null empties the cell rather than leaving what was there. It is the same
 * answer pasting "abc" into a number column gives, and the cell going blank is
 * how somebody sees that what they pasted was not a date.
 *
 * Month names -- "5 March 2024" -- are not read. Upstream reaches for a format
 * guesser for those; the formats here are the ones the field itself displays,
 * plus ISO, which is what a paste out of the API carries.
 */
export function parseDateInput(field: DateFieldLike, text: string): Date | null {
  const trimmed = text.trim()
  if (trimmed === '') return null
  if (ISO_INSTANT.test(trimmed)) return parseDateValue(trimmed)

  const time = TIME_AT_END.exec(trimmed)
  const day = parseDay(
    (time === null ? trimmed : trimmed.slice(0, time.index)).trim(),
    field.date_format ?? 'ISO',
  )
  if (day === null) return null

  // A time on a field that has none is read and then dropped, rather than
  // refused: a column of timestamps pasted into a date column is a column of
  // days, which is what the field can hold.
  const clock = time === null ? { hour: 0, minute: 0, second: 0 } : parseClock(time)
  if (clock === null) return null

  const wall: WallClock = { ...day, ...clock }

  // `getFieldTimezone` guesses the viewer's zone for a field that pins none,
  // which is the same zone the cell is *rendered* in -- so what is stored is
  // what the person reading it saw themselves type. It answers nothing at all
  // for a field with no time, which is the one place the rule lives: a day has
  // no timezone, and `formatDateForApi` takes the day back out of UTC.
  const zone = getFieldTimezone(field) ?? 'UTC'
  return new Date(
    new TZDate(
      wall.year,
      wall.month - 1,
      wall.day,
      wall.hour,
      wall.minute,
      wall.second,
      zone,
    ).getTime(),
  )
}

/**
 * How an instant is sent.
 *
 * A field without time sends the day alone and no timezone at all: a date-only
 * value shifted by an offset lands on the day before or after.
 */
export function formatDateForApi(field: DateFieldLike, date: Date): string {
  const iso = date.toISOString()
  return field.date_include_time === true ? iso : iso.slice(0, 10)
}
